"use client";

import Image from "next/image";
import Link from "next/link";

import { useTranslation } from "@/lib/hooks/use-translation";
import {
  BlogPost,
  formatBlogDate,
  getLocalizedBlogPost,
} from "../data/blog-posts";
import { blogHref } from "../data/paths";

type BlogDetailHeaderProps = {
  post: BlogPost;
};

export function BlogDetailHeader({ post }: BlogDetailHeaderProps) {
  const { language, t } = useTranslation();
  const localizedPost = getLocalizedBlogPost(post, language);
  const { author } = localizedPost;

  // Only worth saying when it is news: a post saved again on the day it went
  // out has not been "updated" in any sense a reader cares about. A visible
  // date is also what lets a reader — and an answer engine — judge whether the
  // advice is still current, which `dateModified` alone doesn't show anyone.
  const updatedDay = localizedPost.updatedAt.slice(0, 10);
  const wasUpdated = updatedDay > localizedPost.publishedAt;

  return (
    <header className="flex flex-col gap-8">
      <nav aria-label="breadcrumb" className="flex flex-wrap items-center gap-3">
        <Link
          href={blogHref(language, "/blog")}
          className="text-base font-medium leading-6 text-black/60"
        >
          {t("blogPage.allArticles")}
        </Link>
        <span className="text-base font-medium leading-6 text-[#0B64F4]">
          &gt;
        </span>
        <span className="max-w-2xl text-base font-medium leading-6 text-black">
          {localizedPost.title}
        </span>
      </nav>

      <div className="flex flex-col gap-10">
        <div className="flex max-w-5xl flex-col gap-2">
          <p className="text-sm font-medium leading-5 text-black/60">
            <time dateTime={localizedPost.publishedAt}>
              {formatBlogDate(localizedPost.publishedAt, language)}
            </time>
            {wasUpdated ? (
              <>
                {" · "}
                {t("blogPage.updatedOn")}{" "}
                <time dateTime={localizedPost.updatedAt}>
                  {formatBlogDate(updatedDay, language)}
                </time>
              </>
            ) : null}
            {localizedPost.category ? ` / ${localizedPost.category}` : ""}
          </p>
          <h1 className="text-balance text-[28px] font-semibold leading-[1.25] text-black md:text-[32px]">
            {localizedPost.title}
          </h1>
          <p className="text-pretty text-xl font-normal leading-8 text-black/60 md:text-2xl">
            {localizedPost.description}
          </p>
          {author ? (
            <p className="mt-2 text-base leading-6 text-black/60">
              {t("blogPage.writtenBy")}{" "}
              <span className="font-semibold text-black">{author.name}</span>
              {author.title ? `, ${author.title}` : ""}
            </p>
          ) : null}
        </div>

        <div className="relative aspect-[16/9] w-full overflow-hidden rounded-[20px] bg-[#F3F4F6]">
          {post.image ? (
            <Image
              src={post.image}
              alt={localizedPost.title}
              fill
              priority
              sizes="(min-width: 1280px) 1280px, 100vw"
              className="object-contain"
            />
          ) : null}
        </div>
      </div>
    </header>
  );
}
